<script>
  // Шторка выбора модели из шапки чата (1.25.7). Модели сгруппированы по провайдерам
  // (OpenAI, Z.AI, Yandex, Home), сверху — недавние. Тап по модели сразу сохраняет её:
  // родитель ловит select и шлёт PUT настроек. Точки состояния — только когда backend
  // их прислал (владельцу сервера), как в строке моделей.
  // События: select({ id }), settings, close.
  import { createEventDispatcher } from 'svelte';
  import BottomSheet from './BottomSheet.svelte';
  import { TEXT } from '../ui-text.js';
  import { groupModels, isModelBlocked, modelHint, modelLabel, providerLabel, recentFromCatalog } from '../model-picker.js';

  export let open = false;
  // [{id, provider, supportsImages, domestic, state}] — /api/models.
  export let models = [];
  export let current = null;
  // Флажок чата «не отдавать российскому резерву»: модели Yandex неактивны.
  export let noDomestic = false;
  export let recent = [];
  // id модели, которая сейчас сохраняется.
  export let busyId = null;
  export let error = '';
  export let showSettings = true;

  const dispatch = createEventDispatcher();

  $: groups = groupModels(models);
  $: recentItems = recentFromCatalog(recent, models);

  function choose(model) {
    if (busyId || isModelBlocked(model, noDomestic)) {
      return;
    }
    dispatch('select', { id: model.id });
  }
</script>

<BottomSheet {open} title={TEXT.modelPicker.title} on:close>
  <div class="picker" data-testid="model-picker">
    {#if error}
      <p class="picker-error" role="alert">{error}</p>
    {/if}
    {#if recentItems.length}
      <div class="picker-group" data-testid="model-picker-recent">
        <div class="picker-group-title">{TEXT.modelPicker.recent}</div>
        {#each recentItems as model (model.id)}
          <button
            type="button"
            class="picker-item"
            data-state={model.state || null}
            data-model-id={model.id}
            aria-current={model.id === current ? 'true' : undefined}
            disabled={isModelBlocked(model, noDomestic) || Boolean(busyId && busyId !== model.id)}
            on:click={() => choose(model)}
          >
            {#if model.state}<span class="dot" aria-hidden="true"></span>{/if}
            <span class="picker-text">
              <span class="picker-name">{modelLabel(model.id)}</span>
              <span class="picker-provider">{providerLabel(model.provider)}</span>
            </span>
            {#if busyId === model.id}
              <span class="picker-mark" aria-hidden="true">…</span>
            {:else if model.id === current}
              <span class="picker-mark" aria-label={TEXT.modelPicker.current}>✓</span>
            {/if}
          </button>
        {/each}
      </div>
    {/if}
    {#each groups as group (group.provider)}
      <div class="picker-group" data-provider={group.provider}>
        <div class="picker-group-title">{group.title}</div>
        {#each group.items as model (model.id)}
          {@const hint = modelHint(model, noDomestic)}
          <button
            type="button"
            class="picker-item"
            data-state={model.state || null}
            data-model-id={model.id}
            aria-current={model.id === current ? 'true' : undefined}
            disabled={isModelBlocked(model, noDomestic) || Boolean(busyId && busyId !== model.id)}
            on:click={() => choose(model)}
          >
            {#if model.state}<span class="dot" aria-hidden="true"></span>{/if}
            <span class="picker-text">
              <span class="picker-name">{modelLabel(model.id)}</span>
              {#if hint}
                <span class="picker-hint">{hint}</span>
              {:else if model.supportsImages}
                <span class="picker-provider">{TEXT.modelPicker.images}</span>
              {/if}
            </span>
            {#if busyId === model.id}
              <span class="picker-mark" aria-hidden="true">…</span>
            {:else if model.id === current}
              <span class="picker-mark" aria-label={TEXT.modelPicker.current}>✓</span>
            {/if}
          </button>
        {/each}
      </div>
    {/each}
    {#if showSettings}
      <button type="button" class="picker-settings" on:click={() => dispatch('settings')}>
        {TEXT.modelPicker.settings}
      </button>
    {/if}
  </div>
</BottomSheet>

<style>
  .picker-error {
    margin: 0.25rem 0.5rem 0.5rem;
    padding: 0.6rem 0.75rem;
    border-radius: 10px;
    background: var(--bg-tertiary);
    color: var(--error-text);
    font-size: 0.875rem;
  }

  .picker-group + .picker-group {
    border-top: 1px solid var(--border-light);
    margin-top: 0.25rem;
    padding-top: 0.25rem;
  }

  .picker-group-title {
    padding: 0.5rem 0.5rem 0.15rem;
    font-size: 0.75rem;
    color: var(--text-muted);
    letter-spacing: 0.02em;
  }

  .picker-item {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    width: 100%;
    min-height: 48px;
    padding: 0.35rem 0.5rem;
    border: none;
    background: none;
    color: var(--text-primary);
    font-size: 1rem;
    text-align: left;
    border-radius: 10px;
  }

  .picker-item[aria-current='true'] {
    background: var(--bg-tertiary);
  }

  .picker-item:disabled {
    opacity: 0.5;
  }

  .picker-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
  }

  .picker-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .picker-provider,
  .picker-hint {
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .picker-item[data-state='down'] .picker-hint {
    color: var(--error-text);
  }

  .picker-mark {
    margin-left: auto;
    color: var(--accent-color);
    font-weight: 600;
  }

  .dot {
    width: 0.5rem;
    height: 0.5rem;
    flex: none;
    border-radius: 50%;
    background: var(--text-tertiary, var(--text-secondary));
    opacity: 0.6;
  }

  [data-state='ok'] > .dot {
    background: var(--success-color, #2e9d5b);
    opacity: 1;
  }

  [data-state='down'] > .dot,
  [data-state='limit'] > .dot {
    background: var(--error-text);
    opacity: 1;
  }

  .picker-settings {
    width: 100%;
    min-height: 44px;
    margin-top: 0.5rem;
    border: none;
    border-top: 1px solid var(--border-light);
    border-radius: 0;
    background: none;
    color: var(--accent-color);
    font-size: 0.9375rem;
  }
</style>
